// Mes rendez-vous : à venir et passés, avec le service, le lieu et l'état ; annulation dans le délai
// de l'agenda (DELETE /usager/rendez-vous/:id) ; prise d'un nouveau rendez-vous.
import { Bandeau, ConfirmationIntegree, EtatVide, LienBouton } from '@cc/ui';
import { CalendarDays } from 'lucide-react';
import { requete } from '../../../api/client';
import { EtatRequete } from '../../../commun/EtatRequete';
import { dateHeure } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { CLES, useMesRendezVous } from '../api';
import { EnTeteCitoyen, ExigeConnexion, Section, TexteStatut } from '../commun';
import type { RendezVousUsager } from '../types';
import styles from '../citoyen.module.css';

export const LIBELLES_ETAT_RDV: Record<string, { libelle: string; ton: 'ok' | 'neutre' | 'attention' | 'info' }> = {
  reserve: { libelle: 'Réservé', ton: 'info' },
  confirme: { libelle: 'Confirmé', ton: 'ok' },
  present: { libelle: 'Honoré', ton: 'ok' },
  absent: { libelle: 'Absent', ton: 'attention' },
  reporte: { libelle: 'Reporté', ton: 'neutre' },
  annule: { libelle: 'Annulé', ton: 'neutre' },
};

function CarteRendezVous({ r }: { r: RendezVousUsager }) {
  const annulation = useAction({ executer: () => requete(`/usager/rendez-vous/${r.id}`, { methode: 'DELETE' }), succes: 'Rendez-vous annulé.', invalider: [[...CLES.rendezVous]] });
  const etat = LIBELLES_ETAT_RDV[r.etat] ?? { libelle: r.etat, ton: 'neutre' as const };
  const annulable = (r.etat === 'confirme' || r.etat === 'reserve') && new Date(r.debut).getTime() > Date.now();
  return (
    <li className={styles.carte} aria-label={`Rendez-vous du ${dateHeure(r.debut)}`}>
      <div className={styles.ligneTexte}>
        <span className={styles.ligneTitre}>{dateHeure(r.debut)}</span>
        <span className={styles.ligneSous}>
          {r.agenda.libelle} · {r.service.nom} · {r.collectivite.nom}
          {r.agenda.lieu ? ` · ${r.agenda.lieu}` : ''}
        </span>
        {r.objet ? <span className={styles.ligneSous}>Objet : {r.objet}</span> : null}
        <TexteStatut libelle={etat.libelle} ton={etat.ton} />
      </div>
      {annulation.messageErreur ? <Bandeau type="erreur">{annulation.messageErreur}</Bandeau> : null}
      {annulable ? (
        <div>
          <ConfirmationIntegree libelle="Annuler ce rendez-vous" petit question="Annuler ce rendez-vous ? Le créneau sera libéré pour une autre personne." libelleConfirmer="Confirmer l'annulation" enCours={annulation.enCours} onConfirmer={() => annulation.lancer(undefined)} />
        </div>
      ) : null}
    </li>
  );
}

export function PageRendezVous() {
  const rdv = useMesRendezVous();
  const maintenant = Date.now();
  const aVenir = (rdv.data ?? []).filter((r) => new Date(r.debut).getTime() >= maintenant && r.etat !== 'annule').sort((a, b) => a.debut.localeCompare(b.debut));
  const passes = (rdv.data ?? []).filter((r) => new Date(r.debut).getTime() < maintenant || r.etat === 'annule').sort((a, b) => b.debut.localeCompare(a.debut));
  return (
    <>
      <EnTeteCitoyen
        titre="Rendez-vous"
        sousTitre="Avec les services de votre collectivité : guichet, urbanisme, finances."
        actions={
          <LienBouton href="/citoyen/rendez-vous/nouveau" variante="principal">
            Prendre rendez-vous
          </LienBouton>
        }
      />
      <ExigeConnexion titre="Connectez-vous pour voir vos rendez-vous">
        <EtatRequete chargement={rdv.isPending} erreur={rdv.error} onReessayer={() => void rdv.refetch()} fonction="Vos rendez-vous" lignes={4}>
          <Section titre="À venir">
            {aVenir.length === 0 ? (
              <EtatVide titre="Aucun rendez-vous à venir" icone={CalendarDays}>
                <p>Choisissez un service et un créneau pour en prendre un. Un rappel vous est envoyé la veille.</p>
              </EtatVide>
            ) : (
              <ul className={styles.pile} aria-label="Rendez-vous à venir">
                {aVenir.map((r) => (
                  <CarteRendezVous key={r.id} r={r} />
                ))}
              </ul>
            )}
          </Section>
          {passes.length > 0 ? (
            <Section titre="Passés ou annulés">
              <ul className={styles.pile} aria-label="Rendez-vous passés ou annulés">
                {passes.map((r) => (
                  <CarteRendezVous key={r.id} r={r} />
                ))}
              </ul>
            </Section>
          ) : null}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
